import React from "react";
import {
  Box,
  Card as MuiCard,
  CardProps as MuiCardProps,
  Divider,
  Stack,
} from "@mui/material";
import { Typography } from "@platform/design-system";

import type { SxProps, Theme } from "@mui/material/styles";

export type CardVariant = "outlined" | "elevated" | "flat";
export type CardPadding = "none" | "condensed" | "normal" | "spacious";

export interface CardProps extends Omit<
  MuiCardProps,
  "variant" | "elevation" | "title"
> {
  /** Visual style. Default: 'outlined' */
  variant?: CardVariant;
  /** Inner padding. Default: 'normal' */
  padding?: CardPadding;
  /** Optional header title */
  title?: React.ReactNode;
  /** Optional header subtitle */
  subtitle?: React.ReactNode;
  /** Optional header action (e.g. a Button or IconButton) */
  action?: React.ReactNode;
  /** Whether to show a divider between header and body. Default: true when title is set */
  headerDivider?: boolean;
}

const variantMap: Record<
  CardVariant,
  { variant: MuiCardProps["variant"]; elevation: number }
> = {
  outlined: { variant: "outlined", elevation: 0 },
  elevated: { variant: "elevation", elevation: 1 },
  flat: { variant: "outlined", elevation: 0 },
};

const paddingMap: Record<CardPadding, number> = {
  none: 0,
  condensed: 1,
  normal: 2,
  spacious: 3,
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = "outlined",
      padding = "normal",
      title,
      subtitle,
      action,
      headerDivider,
      children,
      sx,
      ...rest
    },
    ref,
  ) => {
    const { variant: muiVariant, elevation } = variantMap[variant];
    const paddingSpacing = paddingMap[padding];
    const hasHeader = Boolean(title || subtitle || action);
    const showDivider = headerDivider ?? Boolean(title);
    const shouldTopAlignHeaderAction = Boolean(subtitle);
    const baseSx = {
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
      ...(variant === "flat" && { border: "none" }),
    };

    return (
      <MuiCard
        data-testid="ds-card"
        ref={ref}
        variant={muiVariant}
        elevation={elevation}
        sx={[baseSx, sx] as SxProps<Theme>}
        {...rest}
      >
        {hasHeader && (
          <Stack
            direction="row"
            alignItems={shouldTopAlignHeaderAction ? "flex-start" : "center"}
            justifyContent="space-between"
            sx={{
              px: paddingSpacing,
              pt: paddingSpacing,
              pb: showDivider ? paddingSpacing : 0,
            }}
          >
            <Stack spacing={0.25}>
              {title &&
                (typeof title === "string" ? (
                  <Typography variant="header-small" component="h2">
                    {title}
                  </Typography>
                ) : (
                  title
                ))}
              {subtitle &&
                (typeof subtitle === "string" ? (
                  <Typography variant="body-medium" color="textSecondary">
                    {subtitle}
                  </Typography>
                ) : (
                  subtitle
                ))}
            </Stack>
            {action && <div>{action}</div>}
          </Stack>
        )}
        {showDivider && hasHeader && <Divider />}
        <Box data-testid="ds-card-body" sx={{ p: paddingSpacing, flex: 1 }}>
          {children}
        </Box>
      </MuiCard>
    );
  },
);

Card.displayName = "Card";
